import type { ReactNode, MouseEvent } from "react";
import { useRef, useId, useEffect } from "react";
import FocusTrap from "focus-trap-react";
import { useModalStack } from "@/providers/ModalStackProvider";
import { ui } from "@/strings";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  "aria-label"?: string;
  "data-tutorial-target"?: string;
  /** Use "alertdialog" for modals that require user action before continuing */
  role?: "dialog" | "alertdialog";
}

const modalSizes = {
  sm: "w-full max-w-sm",
  md: "w-full max-w-lg",
  lg: "w-full max-w-2xl",
  xl: "w-full max-w-4xl",
  full: "w-full h-full",
} as const;

export function Modal({ isOpen, onClose, children, size = "md", "aria-label": ariaLabel, "data-tutorial-target": tutorialTarget, role = "dialog" }: ModalProps) {
  const modalId = useId();
  const { registerModal, unregisterModal, isTopmost, getZIndex } = useModalStack();
  const mouseDownOnBackdrop = useRef(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Register modal with stack and capture focus element
  useEffect(() => {
    if (isOpen) {
      const previousElement = document.activeElement as HTMLElement;
      registerModal(modalId, previousElement);
      return () => {
        unregisterModal(modalId);
      };
    }
  }, [isOpen, modalId, registerModal, unregisterModal]);

  // Escape key handler - only for topmost modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      // Check isTopmost at event time, not effect setup time
      if (event.key === "Escape" && isTopmost(modalId)) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isTopmost, modalId, onClose]);

  // Cmd+Enter / Ctrl+Enter to submit form - only for topmost modal
  useEffect(() => {
    if (!isOpen) return;

    const handleSubmitShortcut = (event: KeyboardEvent) => {
      // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
      // Check isTopmost at event time, not effect setup time
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && isTopmost(modalId)) {
        const modalContent = modalContentRef.current;
        if (!modalContent) return;

        // Find the first form in the modal
        const form = modalContent.querySelector("form");
        if (form) {
          // Use requestSubmit() to trigger validation and submit handler
          event.preventDefault();
          form.requestSubmit();
          return;
        }

        // If no form, try to find the action button
        // Strategy: look for the last enabled button that isn't a cancel/close button
        const allButtons = Array.from(
          modalContent.querySelectorAll<HTMLButtonElement>("button")
        );

        // Filter out disabled buttons and likely cancel buttons
        const actionButtons = allButtons.filter((button) => {
          if (button.disabled) return false;

          const text = button.textContent?.toLowerCase() || "";
          const isCancel = text.includes("cancel") || text.includes("close");

          return !isCancel;
        });

        // Use the last action button (typically the primary action)
        const submitButton = actionButtons[actionButtons.length - 1];
        if (submitButton) {
          event.preventDefault();
          submitButton.click();
        }
      }
    };

    document.addEventListener("keydown", handleSubmitShortcut);
    return () => {
      document.removeEventListener("keydown", handleSubmitShortcut);
    };
  }, [isOpen, isTopmost, modalId]);

  if (!isOpen) return null;

  const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    // Track if mousedown happened on the backdrop
    if (e.target === e.currentTarget) {
      mouseDownOnBackdrop.current = true;
    }
  };

  const handleBackdropMouseUp = (e: MouseEvent<HTMLDivElement>) => {
    // Only close if both mousedown and mouseup happened on the backdrop AND this is the topmost modal
    if (e.target === e.currentTarget && mouseDownOnBackdrop.current && isTopmost(modalId)) {
      onClose();
    }
    // Reset the flag
    mouseDownOnBackdrop.current = false;
  };

  const modalZIndex = getZIndex(modalId);

  const modalPadding = size === "sm" ? "px-4 py-4 sm:px-8 sm:py-6" : "p-4 sm:p-6";

  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: false, // We handle escape via our own handler
        initialFocus: false, // Let autofocus work naturally
      }}
    >
      <div
        className="fixed inset-0 flex items-center justify-center bg-brand-950/60 p-4"
        style={{ zIndex: modalZIndex }}
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel}
        data-tutorial-target={tutorialTarget}
      >
        <div
          ref={modalContentRef}
          className={`relative flex flex-col gap-4 rounded-2xl bg-white ${modalPadding} shadow-modal ${modalSizes[size]} max-h-[85vh] overflow-hidden [&>form]:flex [&>form]:flex-col [&>form]:gap-4 [&>form]:min-h-0 [&>form]:flex-1 [&>form]:overflow-hidden`}
        >
          {children}
        </div>
      </div>
    </FocusTrap>
  );
}

interface ModalHeaderProps {
  title: ReactNode;
  onClose?: () => void;
}

export function ModalHeader({ title, onClose }: ModalHeaderProps) {
  return (
    <div className="flex items-center flex-shrink-0">
      <h2 className="text-xl sm:text-2xl font-bold text-text-theme-primary pr-8">{title}</h2>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 text-text-theme-muted hover:text-text-theme-secondary text-2xl leading-none p-0 w-6 h-6 flex items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label={ui.actions.close}
        >
          {/* eslint-disable-next-line no-restricted-syntax */}
          <span aria-hidden="true">✕</span>
        </button>
      )}
    </div>
  );
}

interface ModalBodyProps {
  children: ReactNode;
}

export function ModalBody({ children }: ModalBodyProps) {
  return <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 p-[3px]">{children}</div>;
}

interface ModalFooterProps {
  children: ReactNode;
}

export function ModalFooter({ children }: ModalFooterProps) {
  return <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 flex-shrink-0">{children}</div>;
}
